import type {CampaignApplicationChatSerializer, ChatUserSerializer, MessageSerializer} from "@/generated/DjangoTypes";
import type {TChatSocketMessage} from "@/lib/chatSocket";
import {queryClient} from "@/queryClient";
import type {TPaginated} from "@/store/types";
import type {InfiniteData} from "@tanstack/react-query";
import type {AxiosResponse} from "axios";
import {produce} from "immer";

type TMessagePage = AxiosResponse<TPaginated<MessageSerializer>>;

// The message-list cache is keyed by the paginated_messages URL. The main thread
// view (MessagesSection) is an infinite query keyed `[url]`; the Action Queue
// drawer is a flat query keyed `[url, "drawer"]`. Both must be patched, and both
// carry incompatible shapes, so each is handled separately.
const parseThreadRecipientId = (queryKey: readonly unknown[], campaignId: number): number | null => {
  const url = queryKey[0];
  if (typeof url !== "string" || !url.startsWith("/chat/paginated_messages/")) return null;
  const params = new URLSearchParams(url.split("?")[1] ?? "");
  if (Number(params.get("campaign_id")) !== campaignId) return null;
  return Number(params.get("recipient_id")) || null;
};

const isMessageListKey = (
  queryKey: readonly unknown[],
  campaignId: number,
  message: MessageSerializer,
  drawer: boolean,
): boolean => {
  if ((queryKey.length === 2 && queryKey[1] === "drawer") !== drawer) return false;
  const recipientId = parseThreadRecipientId(queryKey, campaignId);
  return recipientId !== null && (message.sender.id === recipientId || message.recipient.id === recipientId);
};

const handleMessageCreated = (event: TChatSocketMessage): void => {
  const message = event.message as MessageSerializer;
  const campaignId = event.campaign_id as number;
  queryClient.setQueriesData<InfiniteData<TMessagePage>>(
    {predicate: query => isMessageListKey(query.queryKey, campaignId, message, false)},
    cache =>
      !cache
        ? cache
        : produce(cache, draft => {
            if (draft.pages.some(page => page.data.results.some(result => result.id === message.id))) return;
            draft.pages[0]?.data.results.unshift(message);
          }),
  );
  queryClient.setQueriesData<TMessagePage>(
    {predicate: query => isMessageListKey(query.queryKey, campaignId, message, true)},
    cache =>
      !cache
        ? cache
        : produce(cache, draft => {
            if (draft.data.results.some(result => result.id === message.id)) return;
            draft.data.results.unshift(message);
          }),
  );
};

const handleMessageUpdated = (event: TChatSocketMessage): void => {
  const message = event.message as MessageSerializer;
  const campaignId = event.campaign_id as number;
  const applyEdit = (results: MessageSerializer[]): void => {
    const target = results.find(result => result.id === message.id);
    if (!target) return;
    target.text = message.text;
    target.file = message.file;
    target.edited_at = message.edited_at;
    target.deleted_at = message.deleted_at;
  };
  queryClient.setQueriesData<InfiniteData<TMessagePage>>(
    {predicate: query => isMessageListKey(query.queryKey, campaignId, message, false)},
    cache => (!cache ? cache : produce(cache, draft => draft.pages.forEach(page => applyEdit(page.data.results)))),
  );
  queryClient.setQueriesData<TMessagePage>(
    {predicate: query => isMessageListKey(query.queryKey, campaignId, message, true)},
    cache => (!cache ? cache : produce(cache, draft => applyEdit(draft.data.results))),
  );
};

// Exported so the reconnect handler (useChatThreadSubscription) can refresh the
// authoritative badge/notification counts after the socket comes back.
export const invalidateChatCounts = (): void => {
  queryClient.invalidateQueries({
    predicate: query => {
      const url = query.queryKey[0];
      return typeof url === "string" && (url.includes("/message_counts/") || url.includes("/notifications_count/"));
    },
  });
};

type TChatListLastMessage = {
  created: string | null;
  deleted_at: string | null;
  edited_at: string | null;
  id: number;
  sender_id: number;
  text: string;
};

const handleChatListUpdated = (event: TChatSocketMessage): void => {
  const campaignId = event.campaign_id as number;
  const influencerUserId = event.influencer_user_id as number;
  const unreadByCompany = event.unread_by_company as number | null;
  const unreadByInfluencer = event.unread_by_influencer as number | null;
  const lastMessage = event.last_message as TChatListLastMessage | null;

  // Patch the per-campaign thread list in place rather than invalidating it: a
  // refetch flips its fetchStatus to "fetching", which the composer watches and
  // briefly disables itself over (the "select someone to message" flicker).
  queryClient.setQueriesData<InfiniteData<AxiosResponse<TPaginated<CampaignApplicationChatSerializer>>>>(
    {
      predicate: query => {
        const url = query.queryKey[0];
        return typeof url === "string" && url.startsWith(`/chat/company_chat/?campaign_id=${campaignId}`);
      },
    },
    cache =>
      !cache
        ? cache
        : produce(cache, draft => {
            draft.pages.forEach(page => {
              const row = page.data.results.find(result => result.user.id === influencerUserId);
              if (!row) return;
              row.unread_by_company = unreadByCompany;
              row.unread_by_influencer = unreadByInfluencer;
              if (lastMessage) {
                row.last_message = {
                  created: lastMessage.created ?? undefined,
                  deleted_at: lastMessage.deleted_at,
                  edited_at: lastMessage.edited_at,
                  id: lastMessage.id,
                  sender: {id: lastMessage.sender_id} as ChatUserSerializer,
                  text: lastMessage.text,
                };
              }
            });
          }),
  );

  // The left-rail campaign list has no per-campaign total in the payload, so it
  // must refetch. refetchType "all" so it refreshes even when it's inactive
  // (message arrived while off the chat page) — the default "active" would leave
  // it stale, and refetchOnMount is off globally, so it wouldn't refresh on
  // return. The composer doesn't depend on it, so refetching is flicker-free.
  queryClient.invalidateQueries({
    predicate: query => {
      const url = query.queryKey[0];
      return typeof url === "string" && url.includes("/chat_campaigns/");
    },
    refetchType: "all",
  });

  // The "needs reply" sidebar can't be patched in place: a thread enters or
  // leaves the list (and its row's last message changes) based on read + pending
  // state the server computes, so we invalidate it to refetch. Its fetchStatus
  // isn't watched by the composer, so no flicker; refetchType "all" so it also
  // refreshes while a different campaign's chat is the active view.
  queryClient.invalidateQueries({
    predicate: query => {
      const url = query.queryKey[0];
      return typeof url === "string" && url.startsWith("/chat/needs_reply/");
    },
    refetchType: "all",
  });
};

// Route an inbound WebSocket frame into the TanStack Query cache. REST remains
// the source of truth: message events patch the open thread in place, while the
// badge/list nudges refetch their authoritative endpoints.
export const applyChatSocketEvent = (event: TChatSocketMessage): void => {
  switch (event.type) {
    case "message.created":
      handleMessageCreated(event);
      break;
    case "message.edited":
    case "message.deleted":
      handleMessageUpdated(event);
      break;
    case "unread.counts":
      invalidateChatCounts();
      break;
    case "chat_list.updated":
      handleChatListUpdated(event);
      break;
  }
};
